import React, { FC, useState } from 'react';
import { Box, Button, Card, CardContent, Typography, makeStyles, Theme, Link } from '@material-ui/core';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import EquipmentIcon from '@material-ui/icons/Build';
import { ucWords } from 'utils';
import useOpenRecord from 'hooks/useOpenRecord';
import { TabRecordType } from 'constants/tabs';
import ShowPdfModal from 'components/ShowPdfModal';
import { ShowPdfTypes } from 'constants/enum';
import MediaPreview from 'components/MediaPreview';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    borderRadius: 24,
    boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
    marginBottom: theme.spacing(3),
    background: '#fff',
    margin: theme.spacing(2)
  },
  content: {
    display: 'grid',
    gridTemplateColumns: '220px 1fr',
    gap: theme.spacing(4),
    padding: theme.spacing(3.5),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  },
  left: {
    borderRight: '1px solid #eef1f3',
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      borderRight: 0,
      borderBottom: '1px solid #eef1f3',
      paddingBottom: theme.spacing(2)
    }
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    color: '#8a949e',
    letterSpacing: 1,
    textTransform: 'uppercase'
  },
  jobId: {
    color: '#2178a8',
    fontSize: 16,
    fontWeight: 800,
    marginBottom: theme.spacing(3)
  },
  value: {
    color: '#3d454d',
    fontSize: 14,
    fontWeight: 700,
    marginBottom: theme.spacing(3)
  },
  iconLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: '#3d454d',
    fontSize: 13,
    fontWeight: 600,
    marginTop: 4
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    alignItems: 'flex-start'
  },
  tech: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center'
  },
  avatar: {
    width: 38,
    height: 38,
    borderRadius: 8
  },
  techName: {
    fontSize: 14,
    fontWeight: 800,
    color: '#2d343b'
  },
  role: {
    fontSize: 11,
    color: '#7a8490'
  },
  datePill: {
    background: '#f5f6f7',
    color: '#6b737c',
    borderRadius: 18,
    padding: '7px 14px',
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: 'nowrap'
  },
  note: {
    marginTop: theme.spacing(2),
    borderLeft: '2px solid #c6dfe8',
    paddingLeft: theme.spacing(2),
    color: '#4c555e',
    lineHeight: 1.65,
    fontStyle: 'italic'
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(4)
  },
  actionBtn: {
    background: '#f2f4f5',
    color: '#4b545c',
    fontSize: 11,
    fontWeight: 800,
    borderRadius: 4,
    padding: '7px 14px',
    boxShadow: 'none',
    '&:hover': {
      background: '#e8ecef',
      boxShadow: 'none'
    }
  },
  equipmentList: {
    marginTop: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  equipmentItem: {
    fontSize: 13,
    color: '#5a6570',
    lineHeight: 1.5,
    display: 'flex',
    alignItems: 'flex-start'
  },

  equipmentBullet: {
    marginRight: theme.spacing(1),
    color: '#0C7792',
    fontWeight: 700
  }
}));

interface Equipment {
  id: number;
  brand: string;
  model: string;
  serialNumber: string;
  location: string;
}

export interface JobNotesCardData {
  id: number | string;
  jobId: number | string;
  serviceType?: string;
  serviceAddress?: string;
  serviceId?: number;
  technicianName: string;
  notes: string;
  createdAt: string | Date;
  photoCount?: number;
  JobNoteMedia?: any[];
  Equipments?: Equipment[] | null;
}

interface JobNotesCardProps {
  note: JobNotesCardData;
  handleSnackbar: (variant: 'success' | 'error', message: string, isCountdown?: boolean) => void;
}

const formatDate = (date: string | Date) =>
  new Date(date).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

const JobNotesCard: FC<JobNotesCardProps> = ({ note, handleSnackbar }) => {
  const openRecord = useOpenRecord();
  const classes = useStyles();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openViewModal, setOpenViewModal] = useState<boolean>(false);
  const [selectedMedia, setSelectedMedia] = useState<JobNoteMediaModel[]>([]);

  const handleViewJob = (jobId: number): React.MouseEventHandler => () => {
    openRecord({ recordType: TabRecordType.JOB, recordId: jobId });
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleOpenViewModal = () => {
    const media = note.JobNoteMedia!;

    setOpenViewModal(true);
    setSelectedMedia(media);
  };

  const renderEquipment = () => {
    if (!note.Equipments || note.Equipments.length === 0) {
      return <Typography className={classes.equipmentItem}>-</Typography>;
    }

    return (
      <Box className={classes.equipmentList}>
        {note.Equipments.map(equip => (
          <Typography key={equip.id} className={classes.equipmentItem}>
            {`${equip.brand} ${equip.model}, ${equip.serialNumber}-${equip.location}`}
          </Typography>
        ))}
      </Box>
    );
  };

  return (
    <Card className={classes.card}>
      <CardContent className={classes.content}>
        <Box className={classes.left}>
          <Typography className={classes.label}>Job ID</Typography>
          <Link component='button' color='primary' style={{ textAlign: 'left' }} onClick={handleViewJob(Number(note.jobId))}>
            <Typography className={classes.jobId}>{`#${note.jobId}`}</Typography>
          </Link>

          <Typography className={classes.label}>Service Type</Typography>
          <Typography className={classes.value}>{ucWords(note.serviceType === 'CONTRACT' ? 'SERVICE CONTRACT' : 'AD-HOC SERVICE') || '-'}</Typography>

          <Box className={classes.iconLine}>
            <LocationOnIcon fontSize='small' />
            <Typography variant='body2'>{note.serviceAddress || '-'}</Typography>
          </Box>

          <Box className={classes.iconLine}>
            <EquipmentIcon fontSize='small' />
            {renderEquipment()}
          </Box>
        </Box>

        <Box>
          <Box className={classes.topRow}>
            <Box className={classes.tech}>
              <Box>
                <Typography className={classes.techName}>{note.technicianName}</Typography>
              </Box>
            </Box>

            <Box className={classes.datePill}>{formatDate(note.createdAt)}</Box>
          </Box>

          <Typography variant='body1' className={classes.note}>
            {note.notes}
          </Typography>

          <Box className={classes.actions}>
            {!!note.photoCount && (
              <Button className={classes.actionBtn} size='small' variant='contained' onClick={() => handleOpenViewModal()}>
                View Photo ({note.photoCount})
              </Button>
            )}

            <Button className={classes.actionBtn} size='small' variant='contained' onClick={() => handleOpenModal()}>
              Service Report
            </Button>
          </Box>
        </Box>
      </CardContent>
      {openViewModal && <MediaPreview open={openViewModal} setOpen={setOpenViewModal} media={selectedMedia} />}

      {openModal && (
        <ShowPdfModal
          id={Number(note.jobId)}
          serviceId={Number(note.serviceId)}
          documentType={ShowPdfTypes.JOB}
          documentName={`${note.jobId}`}
          open={openModal}
          handleClose={() => setOpenModal(false)}
          handleSnackbar={handleSnackbar}
        />
      )}
    </Card>
  );
};

export default JobNotesCard;
